import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import {
  budgetRecurrenceTypeValues,
  budgetTypeValues,
  budgetUpsertSchema,
  revenueCategoryValues,
} from "@shared/contracts/commercial";

type ContractOption = {
  id: string;
  name: string;
  client: { name: string };
};

type ServiceOption = {
  id: string;
  customName?: string | null;
  product: { name: string };
  client: { name: string };
};

type FormValues = {
  contractId?: string;
  serviceId?: string;
  name: string;
  budgetType: (typeof budgetTypeValues)[number];
  recurrenceType: (typeof budgetRecurrenceTypeValues)[number];
  recurrenceIntervalMonths?: string;
  startDate: string;
  endDate?: string;
  revenueCategory: (typeof revenueCategoryValues)[number];
  rolloverEnabled: boolean;
  rolloverCapMinutes?: string;
  budgetedMinutes?: string;
  budgetedAmount?: string;
  currency: string;
  notes?: string;
};

interface BudgetFormProps {
  defaultValues?: Partial<FormValues>;
  fixedContractId?: string;
  fixedServiceId?: string;
  onSubmit: (data: Record<string, unknown>) => void;
  isPending?: boolean;
}

export function BudgetForm({
  defaultValues,
  fixedContractId,
  fixedServiceId,
  onSubmit,
  isPending,
}: BudgetFormProps) {
  const { data: contracts } = useQuery<ContractOption[]>({
    queryKey: ["/api/contracts"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: services } = useQuery<ServiceOption[]>({
    queryKey: ["/api/billing/client-services"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(budgetUpsertSchema as any),
    defaultValues: {
      contractId: fixedContractId ?? "",
      serviceId: fixedServiceId ?? "",
      name: "",
      budgetType: "recurring_services",
      recurrenceType: "monthly",
      recurrenceIntervalMonths: "1",
      startDate: "",
      endDate: "",
      revenueCategory: "recurring",
      rolloverEnabled: false,
      rolloverCapMinutes: "",
      budgetedMinutes: "",
      budgetedAmount: "",
      currency: "GBP",
      notes: "",
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) =>
        onSubmit({
          ...values,
          contractId: values.contractId || null,
          serviceId: values.serviceId || null,
          recurrenceIntervalMonths: values.recurrenceIntervalMonths
              ? Number(values.recurrenceIntervalMonths)
              : null,
            rolloverCapMinutes: values.rolloverCapMinutes
              ? Number(values.rolloverCapMinutes)
              : null,
            budgetedMinutes: values.budgetedMinutes
              ? Number(values.budgetedMinutes)
              : null,
            budgetedAmount: values.budgetedAmount || null,
            endDate: values.endDate || null,
          }),
        )}
        className="space-y-4"
      >
        {!fixedContractId && (
          <FormField
            control={form.control}
            name="contractId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? "" : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No contract</SelectItem>
                    {(contracts ?? []).map((contract) => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {contract.name} · {contract.client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        )}

        {!fixedServiceId && (
          <FormField
            control={form.control}
            name="serviceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Owning Service</FormLabel>
                <Select
                  value={field.value || "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? "" : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional owning service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No owning service</SelectItem>
                    {(services ?? []).map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {(service.customName || service.product.name) ?? service.id} · {service.client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Budget name</FormLabel>
              <FormControl>
                <Input placeholder="Support Retainer" {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="budgetType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {budgetTypeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="recurrenceType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Recurrence</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {budgetRecurrenceTypeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="revenueCategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Revenue category</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {revenueCategoryValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="recurrenceIntervalMonths"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Interval months</FormLabel>
                <FormControl>
                  <Input type="number" min="1" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="budgetedMinutes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budgeted minutes</FormLabel>
                <FormControl>
                  <Input type="number" min="0" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="budgetedAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budgeted amount</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rolloverCapMinutes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rollover cap minutes</FormLabel>
                <FormControl>
                  <Input type="number" min="0" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="rolloverEnabled"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel>Rollover enabled</FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea {...field} value={field.value ?? ""} className="min-h-[90px]" />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save budget
          </Button>
        </div>
      </form>
    </Form>
  );
}
